import {
	overlay,
	Overlay,
	OverlayOptions,
	ConnectedPositionStrategy,
	HorizontalConnectionPos,
	VerticalConnectionPos,
	OverlayPositionBuilder,
} from '../overlay';
import { KEYCODES } from '@opten/keycodes';
import { getSelectorElementFromElement } from '@opten/dom/get-selector-element-from-element';
import { eventNamespaceFactory } from '@opten/events/event-namespace-factory';
import { Menu } from './menu';
import { RepositionScrollStrategy } from '../overlay/scroll/reposition-scroll-strategy';

export class MenuTriggerOptions {
	menuElementSelector?: (element: HTMLElement) => HTMLElement;
}

export class MenuTrigger {
	private overlayRef: Overlay;
	private menuOpen = false;
	private eventNamespaceFn = eventNamespaceFactory('opten.menu');

	// todo: can we use @ComponentRef? or somethin like Angular Material?
	menu: Menu;

	protected options: MenuTriggerOptions;

	private defaults: MenuTriggerOptions = {
		menuElementSelector: (element) =>
			getSelectorElementFromElement(element, 'data-target', true),
	};

	constructor(protected element: HTMLElement, options?: MenuTriggerOptions) {
		this.options = { ...this.defaults, ...options };
		this.init();
	}

	protected init() {
		const menuElement = this.options.menuElementSelector(this.element);

		if (menuElement) {
			this.menu = new Menu(menuElement);
		}
	}

	open(): void {
		if (this.menuOpen || !this.menu) {
			return;
		}

		this.menuOpen = true;

		if (this.menu.lazyContent) {
			this.menu.updatePortal();
		}

		this.createOverlay().open();

		this.eventNamespaceFn(this.element, 'open');
	}

	close(): void {
		if (this.menuOpen && this.menu) {
			this.overlayRef.close();
		}
	}

	toggle() {
		this.menuOpen ? this.close() : this.open();
	}

	/**
	 * This method creates the overlay from the provided menu's template and saves its
	 * OverlayRef so that it can be attached to the DOM when open is called.
	 */
	private createOverlay(): Overlay {
		if (!this.overlayRef) {
			const options: OverlayOptions = {
				scrollStrategy: new RepositionScrollStrategy(),
				positionStrategy: this._getPosition(),
				hasBackdrop: this.menu.hasBackdrop || true,
				backdropClassName:
					this.menu.backdropClassName || 'c-overlay-transparent-backdrop',
			};

			this._subscribeToPositions(
				options.positionStrategy as ConnectedPositionStrategy
			);

			this.overlayRef = overlay(this.menu.portalElement, options);

			this.overlayRef.backdropClick = () => this.close();

			this.overlayRef.afterOpened = () =>
				this.eventNamespaceFn(this.element, 'opened');

			this.overlayRef.afterClosed = () => {
				this.eventNamespaceFn(this.element, 'closed');
				this.overlayRef.dispose();
			};

			// todo: better way? we need that here because of the RepositionScrollStrategy
			this.overlayRef.onDetached = () => {
				this.menuOpen = false;
				this.eventNamespaceFn(this.element, 'close');
			};

			// close the menu, doesn't matter what's clicked!
			if (this.overlayRef.portalElement) {
				this.overlayRef.portalElement.addEventListener('click', () =>
					this.close()
				);
			}

			this.overlayRef.onKeyboardEvent = (event) => {
				if (event.keyCode === KEYCODES.ESCAPE) {
					this.close();
				}
			};
		}

		return this.overlayRef;
	}

	private _getPosition(): ConnectedPositionStrategy {
		const [originX, originFallbackX]: HorizontalConnectionPos[] =
			this.menu.xPosition === 'before' ? ['end', 'start'] : ['start', 'end'];

		const [overlayY, overlayFallbackY]: VerticalConnectionPos[] =
			this.menu.yPosition === 'above' ? ['bottom', 'top'] : ['top', 'bottom'];

		let [originY, originFallbackY] = [overlayY, overlayFallbackY];
		const [overlayX, overlayFallbackX] = [originX, originFallbackX];
		const offsetY = 0;

		if (!this.menu.overlapTrigger) {
			originY = overlayY === 'top' ? 'bottom' : 'top';
			originFallbackY = overlayFallbackY === 'top' ? 'bottom' : 'top';
		}

		// todo: like angular material this.overlayRef.position()?
		return new OverlayPositionBuilder()
			.connectedTo(
				this.menu.connectTo ? this.menu.connectTo : this.element,
				{ originX, originY },
				{ overlayX, overlayY }
			)
			.withOffsetY(offsetY)
			.withFallbackPosition(
				{ originX: originFallbackX, originY },
				{ overlayX: overlayFallbackX, overlayY }
			)
			.withFallbackPosition(
				{ originX, originY: originFallbackY },
				{ overlayX, overlayY: overlayFallbackY },
				undefined,
				-offsetY
			)
			.withFallbackPosition(
				{ originX: originFallbackX, originY: originFallbackY },
				{ overlayX: overlayFallbackX, overlayY: overlayFallbackY },
				undefined,
				-offsetY
			);
	}

	private _subscribeToPositions(position: ConnectedPositionStrategy): void {
		position.onPositionChange = (change) => {
			const posX =
				change.connectionPair.overlayX === 'start' ? 'after' : 'before';
			const posY = change.connectionPair.overlayY === 'top' ? 'below' : 'above';

			this.overlayRef.portalElement.style.transformOrigin = `${
				posX === 'before' ? 'right' : 'left'
			} ${posY === 'below' ? 'top' : 'bottom'}`;
		};
	}
}

export function menuTrigger(element: HTMLElement): MenuTrigger {
	return new MenuTrigger(element);
}
